import type { PromotionSummary, PromotionDecision, PublicOffer } from '@/types/promotions'
import axios from 'axios'
import { toast } from 'sonner'
import i18n from '@/i18n'
import type {
  Board,
  CreateBoardData,
  Card,
  CreateCardData,
  AnalyticsSummary,
  ApiResponse,
  Plan,
  UserSubscription
} from '@/types'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
})

// Create a public API instance without auth interceptor
export const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const shouldRefreshCredits = (url?: string, responseData?: any) => {
  if (!url?.includes('/ai/')) return false;
  if (url.includes('/ai/credits/')) return false;
  return Boolean(responseData?.credit_info);
}

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
  (response) => {
    if (shouldRefreshCredits(response.config?.url, response.data)) {
      window.dispatchEvent(new CustomEvent('credit-updated'));
    }

    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't auto-redirect for invitation acceptance - let the page handle it
      const isInvitationAccept = error.config?.url?.includes('/invitations/') &&
        (error.config?.url?.includes('/accept') || error.config?.url?.includes('/decline'));

      if (!isInvitationAccept) {
        // Token expired or invalid - redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // If it's an invitation accept, let the error propagate to the component
    }

    // Handle insufficient credits errors (402)
    if (error.response?.status === 402) {
      const errorData = error.response.data
      window.dispatchEvent(new CustomEvent('insufficient-credits', {
        detail: {
          message: errorData.message || errorData.credit_info?.message || errorData.error || 'Insufficient credits for this action.',
          creditInfo: errorData.credit_info
        }
      }))
    }

    // Handle rate limiting (429). AI endpoints already show their own
    // tailored inline message (see AIInsights.tsx and friends) for their
    // own, separate per-minute throttle — skip those here to avoid a
    // redundant/conflicting toast on top of that inline text.
    if (error.response?.status === 429) {
      const url = error.config?.url as string | undefined
      const isAiEndpoint = url?.includes('/ai/') || url?.includes('/ai-insights') || url?.includes('/optimizer/')
      if (!isAiEndpoint) {
        const retryAfter = error.response.headers?.['retry-after']
        const waitMessage = retryAfter
          ? `Please wait about ${retryAfter} seconds and try again.`
          : 'Please wait a moment and try again.'
        toast.error(`Too many attempts. ${waitMessage}`)
      }
    }

    // Handle subscription tier limit errors (403)
    if (error.response?.status === 403) {
      const errorData = error.response.data
      // Check if it's a subscription limit error
      if (errorData.error && (
        errorData.error === 'Board limit reached' ||
        errorData.error === 'Platform not allowed' ||
        errorData.error === 'Account limit exceeded'
      )) {
        // Dispatch a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('subscription-limit-reached', {
          detail: {
            error: errorData.error,
            message: errorData.message,
            currentPlan: errorData.current_plan,
            maxBoards: errorData.max_boards,
            currentBoards: errorData.current_boards,
            platform: errorData.platform
          }
        }))
      }
    }

    return Promise.reject(error)
  }
)

// Board API
export const boardApi = {
  // Get all boards
  getAll: async () => {
    const response = await api.get<ApiResponse<Board[]>>('/boards')
    return response.data
  },

  // Get single board
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<Board>>(`/boards/${id}`)
    return response.data
  },

  // Create board
  create: async (data: CreateBoardData) => {
    const response = await api.post<ApiResponse<Board>>('/boards', data)
    return response.data
  },

  // Update board
  update: async (id: string, data: Partial<CreateBoardData>) => {
    const response = await api.put<ApiResponse<Board>>(`/boards/${id}`, data)
    return response.data
  },

  // Delete board
  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<void>>(`/boards/${id}`)
    return response.data
  },

  // Get board analytics summary
  getAnalytics: async (id: string) => {
    const response = await api.get<ApiResponse<AnalyticsSummary>>(`/boards/${id}/analytics`)
    return response.data
  },

  // Invitation management
  inviteMember: async (id: string, data: { email: string; role: 'editor' | 'viewer' }) => {
    const response = await api.post<ApiResponse<any>>(`/boards/${id}/invitations`, data)
    return response.data
  },

  getInvitations: async (id: string) => {
    const response = await api.get<ApiResponse<{ invitations: any[] }>>(`/boards/${id}/invitations`)
    return response.data
  },

  cancelInvitation: async (invitationId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/invitations/${invitationId}`)
    return response.data
  },

  // Member management
  getMembers: async (id: string) => {
    const response = await api.get<ApiResponse<{ members: any[]; total: number }>>(`/boards/${id}/members`)
    return response.data
  },

  updateMemberRole: async (boardId: string, memberId: string, role: 'editor' | 'viewer') => {
    const response = await api.patch<ApiResponse<any>>(`/boards/${boardId}/members/${memberId}`, { role })
    return response.data
  },

  removeMember: async (boardId: string, memberId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/boards/${boardId}/members/${memberId}`)
    return response.data
  },
}

// AI Content Generation API
export const aiApi = {
  // Generate content ideas
  generateIdeas: async (data: {
    board_id: string
    platform?: 'youtube' | 'instagram' | 'tiktok' | 'all'
    count?: number
    topic?: string
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/content/ideas', data)
    return response.data
  },

  // Generate optimized titles
  generateTitles: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok'
    topic: string
    count?: number
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/content/title', data)
    return response.data
  },

  // Generate description/caption
  generateDescription: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok'
    title: string
    topic?: string
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/content/description', data)
    return response.data
  },

  // Generate script
  generateScript: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok'
    title: string
    outline?: string
    duration?: 'short' | 'medium' | 'long'
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/content/script', data)
    return response.data
  },

  // Generate hashtags
  generateHashtags: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok'
    topic: string
    count?: number
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/content/hashtags', data)
    return response.data
  },

  // Content Optimizer
  analyzeContent: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok'
    title: string
    description?: string
    tags?: string[]
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/optimizer/analyze', data)
    return response.data
  },

  // Get Score for Title
  scoreTitle: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok' | string
    title: string
  }) => {
    const response = await api.post<ApiResponse<{ score: number, suggestion?: string }>>('/ai/optimizer/score/title', data)
    return response.data
  },

  scoreDescription: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok' | string
    description: string
    title: string
  }) => {
    const response = await api.post<ApiResponse<{ score: number, suggestion?: string }>>('/ai/optimizer/score/description', data)
    return response.data
  },

  // Get Scores for Hashtags
  scoreHashtags: async (data: {
    board_id: string
    platform: 'youtube' | 'instagram' | 'tiktok' | string
    hashtags: string[]
    topic: string
    card_id?: string
  }) => {
    const response = await api.post<ApiResponse<Record<string, number>>>('/ai/optimizer/score/hashtags', data)
    return response.data
  },

  // Get AI history
  getHistory: async (params: {
    board_id?: string
    type?: string
    platform?: string
    saved_only?: boolean
    page?: number
  }) => {
    const response = await api.get<ApiResponse<any>>('/ai/content/history', { params })
    return response.data
  },

  // Save/Unsave generated content
  toggleSave: async (id: string) => {
    const response = await api.patch<ApiResponse<any>>(`/ai/content/${id}/save`)
    return response.data
  },

  // Rate content
  rateContent: async (id: string, rating: number) => {
    const response = await api.patch<ApiResponse<any>>(`/ai/content/${id}/rate`, { rating })
    return response.data
  }
}

// Card API
export const cardApi = {
  // Get full card details by ID
  getById: async (cardId: string) => {
    const response = await api.get<ApiResponse<Card>>(`/cards/${cardId}`)
    return response.data
  },

  // Create card in a stage
  create: async (boardId: string, stageId: string, data: CreateCardData) => {
    const response = await api.post<ApiResponse<Card>>(`/boards/${boardId}/stages/${stageId}/cards`, data)
    return response.data
  },

  // Update card
  update: async (boardId: string, stageId: string, cardId: string, data: Partial<CreateCardData>) => {
    const response = await api.patch<ApiResponse<Card>>(`/boards/${boardId}/stages/${stageId}/cards/${cardId}`, data)
    return response.data
  },

  // Delete card
  delete: async (boardId: string, stageId: string, cardId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/boards/${boardId}/stages/${stageId}/cards/${cardId}`)
    return response.data
  },

  // Move card to different stage
  move: async (boardId: string, cardId: string, toStageId: string, position?: number) => {
    const payload: any = {
      stage_id: toStageId,
    }

    // Only include position if it's explicitly provided and greater than 0
    if (position !== undefined && position > 0) {
      payload.position = position
    }

    const response = await api.post<ApiResponse<Card>>(`/boards/${boardId}/cards/${cardId}/move`, payload)
    return response.data
  },

  // Publish / Schedule to Instagram
  instagramPublish: async (cardId: string) => {
    const response = await api.post<ApiResponse<any>>(`/cards/${cardId}/instagram/publish`)
    return response.data
  },
}


// Stage API
export const stageApi = {
  // Create stage in a board
  create: async (boardId: string, data: { name: string; background_color?: string; order?: number }) => {
    const response = await api.post<ApiResponse<any>>(`/boards/${boardId}/stages`, data)
    return response.data
  },

  // Update stage
  update: async (boardId: string, stageId: string, data: { name?: string; background_color?: string; order?: number }) => {
    const response = await api.put<ApiResponse<any>>(`/boards/${boardId}/stages/${stageId}`, data)
    return response.data
  },

  // Delete stage
  delete: async (boardId: string, stageId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/boards/${boardId}/stages/${stageId}`)
    return response.data
  },

  // Reorder stages
  reorder: async (boardId: string, stageOrders: { id: string; order: number }[]) => {
    const response = await api.post<ApiResponse<any>>(`/boards/${boardId}/stages/reorder`, {
      stages: stageOrders
    })
    return response.data
  },
}

// Subscription API
export const subscriptionApi = {
  getPromotions: async () => (await api.get<ApiResponse<PromotionSummary[]>>('/subscription/promotions')).data,
  /** Public: the running Product Hunt offer, or null. Used by the banner for visitors who are not logged in. */
  getCurrentOffer: async () => (await api.get<ApiResponse<PublicOffer | null>>('/promotions/current')).data,
  getPromotion: async (id:string) => (await api.get<ApiResponse<PromotionDecision>>(`/subscription/promotions/${encodeURIComponent(id)}`)).data,
  // Get all available plans (public endpoint - no auth required)
  getPlans: async () => {
    const response = await publicApi.get<ApiResponse<Plan[]>>('/subscription/plans')
    return response.data
  },

  // Get user's current plan and usage
  getCurrent: async () => {
    const response = await api.get<ApiResponse<UserSubscription>>('/subscription/current')
    return response.data
  },

  // Check if action is allowed
  checkLimit: async (action: string, platform?: string) => {
    const response = await api.get<ApiResponse<any>>('/subscription/check-limit', {
      params: { action, platform }
    })
    return response.data
  },

  // Upgrade plan
  upgradePlan: async (planId: string, interval: 'monthly' | 'annually' = 'monthly', promotionId?:string, campaignId?:string) => {
    const response = await api.post<ApiResponse<any>>(`/subscription/upgrade`, { plan_id: planId, interval, ...(promotionId ? {promotion_id:promotionId}:{}), ...(campaignId ? {campaign_id:campaignId}:{}) })
    return response.data
  },

  // Cancel the active subscription (takes effect at the end of the current billing period)
  cancelSubscription: async () => {
    const response = await api.post<ApiResponse<{ access_until?: string | null }>>('/subscription/cancel')
    return response.data
  },

  // Get user's checkout history
  getCheckouts: async (page: number = 1) => {
    const response = await api.get<ApiResponse<{
      current_page: number
      data: Array<{
        id: string
        user_id: string
        plan_id: string
        status: number
        status_label: string
        transaction_id: string | null
        paddle_checkout_id: string | null
        paddle_transaction_id: string | null
        expired_at: string | null
        created_at: string
        updated_at: string
        plan: {
          id: string
          name: string
          tier: string
          price: string
          annual_price?: string | null
        }
      }>
      per_page: number
      total: number
    }>>(`/subscription/checkouts?page=${page}`)
    return response.data
  }
}

// Payment API
export const paymentApi = {
  // Confirm payment
  confirmPayment: async (paymentIntentId: string) => {
    const response = await api.post<ApiResponse<any>>(`/payment/confirm`, { paymentIntentId })
    return response.data
  }
}

// AI Credit API
export const creditApi = {
  // Get user's current AI credit balance and tier
  getBalance: async () => {
    const response = await api.get<ApiResponse<{
      total_balance: number;
      allocation_balance: number;
      topup_balance: number;
      current_tier: string;
      allocation_expires_at: string | null;
    }>>('/ai/credits/balance')
    return response.data
  },

  // Get available top-up packs
  getPacks: async () => {
    const response = await api.get<ApiResponse<Array<{
      id: string;
      label: string;
      credits: number;
      price_usd: number;
      bonus_pct: number;
    }>>>('/ai/credits/packs')
    return response.data
  },

  // Initiate purchase of a top-up pack
  buyTopUp: async (packId: string) => {
    const response = await api.post<ApiResponse<{
      paddle_transaction_id: string;
      approval_url: string;
    }>>('/ai/credits/buy', { pack_id: packId })
    return response.data
  },

  // Get user's credit transaction history
  getHistory: async (page: number = 1) => {
    const response = await api.get<ApiResponse<{
      current_page: number;
      data: Array<{
        id: string;
        user_id: string;
        amount: number;
        transaction_type: 'allocation' | 'topup' | 'deduction' | 'expiration' | 'refund';
        balance_type: 'allocation' | 'topup';
        feature_used: string | null;
        description: string;
        created_at: string;
      }>;
      per_page: number;
      total: number;
      last_page: number;
    }>>(`/ai/credits/history?page=${page}`)
    return response.data
  },

  // Get the credit cost for every AI feature (sourced live from backend config).
  // Call once on login; use the result to display accurate labels in UI buttons.
  getCosts: async (): Promise<Record<string, number>> => {
    const response = await api.get<ApiResponse<Record<string, number>>>('/ai/credits/costs')
    return response.data?.data ?? {}
  },
}

// YouTube OAuth API (Board-level)
export const youtubeOAuthApi = {
  // Get OAuth URL to connect YouTube account for a specific board
  connect: async (boardId: string) => {
    const response = await api.get<ApiResponse<{ url: string }>>(`/youtube/connect?board_id=${boardId}`)
    return response.data
  },

  // Get YouTube connection status for a specific board
  getStatus: async (boardId: string) => {
    const response = await api.get<ApiResponse<{
      connected: boolean
      channel_id: string | null
      channel_name: string | null
      expires_at?: string
      is_expired?: boolean
      needs_reconnect?: boolean
      last_error?: string | null
    }>>(`/youtube/status?board_id=${boardId}`)
    return response.data
  },

  disconnect: async (boardId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/youtube/disconnect?board_id=${boardId}`)
    return response.data
  },
}

// YouTube Publish API
export const youtubePublishApi = {
  // Initiate client-side upload session
  initiateUpload: async (data: {
    board_id: string;
    title: string;
    description?: string;
    tags?: string[];
    content_type?: string;
  }) => {
    const response = await api.post<ApiResponse<{ upload_url: string }>>('/youtube/initiate-upload', data)
    return response.data.data
  },

  // Save the YouTube video ID after upload completes, optionally attaching a custom thumbnail file
  saveVideoId: async (cardId: string, videoId: string, publishDate?: string, thumbnailFile?: File | null) => {
    const formData = new FormData();
    formData.append('video_id', videoId);
    if (publishDate) formData.append('publish_date', publishDate);
    if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

    // Using POST with _method=PATCH because PHP handles multipart/form-data better with POST
    formData.append('_method', 'PATCH');

    const response = await api.post<ApiResponse<any>>(`/cards/${cardId}/video-id`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
}


// Instagram Publish API
export const instagramPublishApi = {
  // Dispatch publish / schedule job for a card
  publish: async (cardId: string, data?: {
    media_type?: 'IMAGE' | 'REELS';
    caption?: string;
    scheduled_publish_time?: number;
    media_url?: string;
  }) => {
    const response = await api.post<ApiResponse<{
      message: string;
      status: string;
      card: any;
    }>>(`/cards/${cardId}/instagram/publish`, data ?? {})
    return response.data.data
  },

  // Cancel a scheduled post
  cancel: async (cardId: string) => {
    const response = await api.delete<ApiResponse<{ message: string; status: string }>>(`/cards/${cardId}/instagram/publish`)
    return response.data.data
  },

  // Poll publish status
  getStatus: async (cardId: string) => {
    const response = await api.get<ApiResponse<{
      status: string;
      publish_date: string | null;
      permalink: string | null;
      instagram_id: string | null;
      has_container: boolean;
    }>>(`/cards/${cardId}/instagram/publish/status`)
    return response.data.data
  },
}

// TikTok Publish API
export const tiktokPublishApi = {
  // Dispatch publish job for a card (video or photo)
  publish: async (cardId: string, data?: {
    scheduled_publish_time?: number;
  }) => {
    const response = await api.post<ApiResponse<{
      message: string;
      status: string;
      card: any;
    }>>(`/cards/${cardId}/tiktok/publish`, data ?? {})
    return response.data.data ?? (response.data as any)
  },

  // Cancel a pending TikTok post
  cancel: async (cardId: string) => {
    const response = await api.delete<ApiResponse<{ message: string; status: string }>>(`/cards/${cardId}/tiktok/publish`)
    return response.data.data ?? (response.data as any)
  },

  // Poll publish status
  getStatus: async (cardId: string) => {
    const response = await api.get<ApiResponse<{
      status: string;
      publish_date: string | null;
      tiktok_url: string | null;
      tiktok_publish_id: string | null;
    }>>(`/cards/${cardId}/tiktok/publish/status`)
    return response.data.data ?? (response.data as any)
  },
}




// YouTube Extended Analytics API
export const youtubeAnalyticsApi = {
  // Get extended YouTube analytics
  getExtended: async (boardId: string, days: number = 30) => {
    const response = await api.get<ApiResponse<{
      channel_id: string
      period: {
        start: string
        end: string
      }
      traffic_sources: Array<{
        source: string
        views: number
        watch_time: number
      }>
      demographics: {
        age_gender: Array<{
          age_group: string
          gender: string
          percentage: number
        }>
        geography: Array<{
          country: string
          country_code: string
          views: number
          watch_time: number
        }>
      }
      devices: Array<{
        device_type: string
        views: number
        watch_time: number
      }>
      audience_retention: Array<{
        date: string
        avg_duration_seconds: number
        avg_percentage: number
      }>
      top_videos: Array<{
        video_id: string
        video_title: string
        video_url: string
        views: number
        likes: number
        comments: number
        shares: number
        watch_time: number
        avg_duration: number
        overall_views: number
        overall_likes: number
        overall_comments: number
      }>
      aggregates: {
        total_watch_time_minutes: number
        average_view_duration: number
        average_view_percentage: number
        subscribers_gained: number
        subscribers_lost: number
        total_shares: number
      }
      sharing_services?: Array<{ service: string; shares: number }>
      end_screen_metrics?: { impressions: number; clicks: number; click_rate: number } | null
      playback_locations?: Array<{ location: string; views: number; watch_time: number }>
      subscriber_status?: Array<{ status: string; views: number; watch_time: number }>
      card_metrics?: { impressions: number; clicks: number; click_rate: number; teaser_impressions: number; teaser_clicks: number; teaser_click_rate: number }
      ad_performance?: { ad_impressions: number; monetized_playbacks: number; cpm: number; playback_based_cpm: number }
      revenue?: { estimated_revenue: number; estimated_ad_revenue: number; estimated_red_partner_revenue: number; gross_revenue: number }
      period_growth?: {
        watch_time: number
        watch_time_percentage: number
        views: number
        views_percentage: number
        subscribers: number
        subscribers_percentage: number
        shares: number
        shares_percentage: number
      }
      synced_at: string
    }>>(`/boards/${boardId}/analytics/youtube/extended?days=${days}`)
    return response.data
  },

  // Get historical YouTube analytics (snapshots over time)
  getHistoricalAnalytics: async (boardId: string, days: number = 30) => {
    const response = await api.get<ApiResponse<import('@/types').YouTubeHistoricalData>>(`/boards/${boardId}/analytics/youtube/history?days=${days}`)
    return response.data
  },

  // Get historical YouTube analytics for a specific video
  getVideoAnalyticsHistory: async (boardId: string, videoId: string, days: number = 30) => {
    const response = await api.get<{
      success: boolean;
      data: {
        snapshots: any[];
        video: { id: string; title: string; published_at: string };
      };
    }>(`/boards/${boardId}/youtube/videos/${videoId}/analytics/history?days=${days}`)
    return response.data
  },

  // Get traffic source breakdown for a specific video
  getVideoTrafficSources: async (boardId: string, videoId: string, days: number = 30) => {
    const response = await api.get<{
      success: boolean;
      data: {
        sources: Array<{
          source_type: string;
          label: string;
          views: number;
          watch_time_minutes: number;
          percentage: number;
        }>;
        total_views: number;
      };
    }>(`/boards/${boardId}/youtube/videos/${videoId}/analytics/traffic-sources?days=${days}`)
    return response.data
  },

  // Get full statistics for a specific video (lazy-loaded data)
  getVideoFullStats: async (boardId: string, videoId: string, days: number = 30) => {
    const response = await api.get<{
      success: boolean;
      data: any;
    }>(`/boards/${boardId}/youtube/videos/${videoId}/full-stats?days=${days}`)
    return response.data
  },


  // Get AI-generated insights for YouTube analytics
  getAIInsights: async (boardId: string, days: number = 30) => {
    const response = await api.get<{
      success: boolean;
      data: {
        insights: Array<{
          icon: string;
          text: string;
          category: string;
          priority: 'high' | 'medium' | 'low';
          actionable: boolean;
        }>;
        summary: string;
      };
      cached: boolean;
      rate_limit?: {
        allowed: boolean;
        remaining: number;
        limit: number;
      };
    }>(`/boards/${boardId}/analytics/youtube/ai-insights?days=${days}`)
    return response.data
  },

  // Get YouTube videos list
  getVideos: async (boardId: string, page: number = 1, period: number = 7) => {
    // Backend returns: { success, data: { paginated }, period_stats }
    const response = await api.get<{
      success: boolean
      data: {
        current_page: number
        data: Array<{
          id: string
          video_id: string
          title: string
          description: string
          thumbnail_url: string
          published_at: string
          duration_seconds: number
          formatted_duration: string
          formatted_avg_duration?: string
          video_url: string
          lifetime_views: number
          lifetime_likes: number
          lifetime_comments: number
          avg_view_duration_seconds?: number
          avg_view_percentage?: number
          stats_updated_at: string
          growth?: {
            views: number
            views_percentage: number
            likes: number
            likes_percentage: number
            comments: number
            comments_percentage: number
            avg_duration: number
            avg_duration_percentage: number
          } | null
          demographics_geography?: any[]
          demographics_age_gender?: any[]
          demographics_devices?: any[]
          demographics_operating_systems?: any[]
          demographics_traffic_sources?: any[]
          demographics_sharing_services?: any[]
          demographics_subscribed_status?: any[]
          demographics_provinces?: any[]
          demographics_live_or_on_demand?: any[]
          demographics_subtitle_languages?: any[]
        }>
        total: number
        per_page: number
        last_page: number
      }
      period_stats?: {
        current: {
          total_views: number
          total_likes: number
          total_comments: number
          total_watch_time_minutes: number
          snapshot_date: string
        }
        previous: {
          total_views: number
          total_likes: number
          total_comments: number
          total_watch_time_minutes: number
          snapshot_date: string
        }
        growth: {
          views: number
          views_percentage: number
          likes: number
          likes_percentage: number
          comments: number
          comments_percentage: number
          watch_time: number
          watch_time_percentage: number
        }
      }
    }>(`/boards/${boardId}/youtube/videos?page=${page}&period=${period}`)
    return response.data
  },

  // Get performance trends for charts (per-video or overall)
  getPerformanceTrends: async (boardId: string, days: number = 30, videoId?: string) => {
    const params = new URLSearchParams({ days: days.toString() })
    if (videoId) {
      params.append('video_id', videoId)
    }
    const response = await api.get<ApiResponse<{
      views: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      likes: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      comments: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
    }>>(`/boards/${boardId}/analytics/youtube/performance-trends?${params}`)
    return response.data
  },
}

// Instagram OAuth API (Board-level, Business Login for Instagram)
export const instagramOAuthApi = {
  // Get OAuth URL to connect Instagram account for a specific board
  connect: async (boardId: string, reconnect: boolean = false) => {
    const url = `/instagram-oauth/connect?board_id=${boardId}${reconnect ? '&reconnect=true' : ''}`;
    const response = await api.get<ApiResponse<{ url: string }>>(url);
    return response.data;
  },

  // Get Instagram connection status for a specific board
  getStatus: async (boardId: string) => {
    const response = await api.get<ApiResponse<{
      connected: boolean
      instagram_user_id: string | null
      username: string | null
      account_type?: string
      expires_at?: string
      is_expired?: boolean
      sync_status?: string | null
      sync_error?: string | null
      needs_reconnect?: boolean
      can_manage_connection?: boolean
      created_at?: string
    }>>(`/instagram-oauth/status?board_id=${boardId}`)
    return response.data
  },

  // Disconnect Instagram account from a specific board
  disconnect: async (boardId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/instagram-oauth/disconnect?board_id=${boardId}`)
    return response.data
  },
}

// TikTok OAuth API
export const tiktokOAuthApi = {
  // Get OAuth URL to connect TikTok account for a specific board
  connect: async (boardId: string) => {
    const response = await api.get<ApiResponse<{ auth_url: string }>>(`/tiktok/connect?board_id=${boardId}`)
    return response.data
  },

  // Get TikTok connection status for a specific board
  getStatus: async (boardId: string) => {
    const response = await api.get<ApiResponse<{
      connected: boolean
      tiktok_user_id: string | null
      username: string | null
      open_id?: string
      expires_at?: string
      is_expired?: boolean
      created_at?: string
      needs_reconnect?: boolean
      last_error?: string | null
    }>>(`/tiktok/boards/${boardId}/status`)
    return response.data
  },

  // Disconnect TikTok account from a specific board
  disconnect: async (boardId: string) => {
    const response = await api.delete<ApiResponse<void>>(`/tiktok/boards/${boardId}/disconnect`)
    return response.data
  },

  // Refresh TikTok access token
  refreshToken: async (boardId: string) => {
    const response = await api.post<ApiResponse<{ expires_at: string }>>(`/tiktok/boards/${boardId}/refresh-token`)
    return response.data
  },
}

// TikTok Analytics API
export const tiktokAnalyticsApi = {
  // Get TikTok analytics for a board
  getAnalytics: async (boardId: string, days: number = 30, page: number = 1) => {
    const response = await api.get<ApiResponse<{
      summary: {
        total_videos: number
        total_views: number
        total_likes: number
        total_comments: number
        total_shares: number
        avg_engagement_rate: number
      }
      recent_videos: Array<{
        id: string
        video_id: string
        title: string | null
        video_description: string | null
        cover_image_url: string | null
        share_url: string | null
        duration: number | null
        published_at: string | null
        like_count: number
        comment_count: number
        share_count: number
        view_count: number
        engagement_rate: number
        video_status: string
        is_stem_verified: boolean
        last_30_days_growth?: {
          views: number
          likes: number
          comments: number
          shares: number
          views_percentage: number
          likes_percentage: number
          comments_percentage: number
          shares_percentage: number
        }
      }>
      connection: {
        username: string
        last_synced_at: string | null
        profile_name: string | null
        profile_username: string | null
        profile_image_url: string | null
        followers_count: number
        total_posts: number
        video_count: number
        total_likes: number
      }
    }>>(`/boards/${boardId}/tiktok/analytics?days=${days}&page=${page}`)
    return response.data
  },

  // Get detailed analytics for a specific video
  getVideoAnalytics: async (videoId: string) => {
    const response = await api.get<ApiResponse<{
      video: {
        id: string
        video_id: string
        title: string | null
        video_description: string | null
        cover_image_url: string | null
        share_url: string | null
        duration: number | null
        published_at: string | null
        like_count: number
        comment_count: number
        share_count: number
        view_count: number
        engagement_rate: number
        video_status: string
        is_stem_verified: boolean
        last_synced_at: string | null
      }
      snapshots: Array<{
        date: string
        view_count: number
        like_count: number
        comment_count: number
        share_count: number
        engagement_rate: number
        view_count_change: number
        like_count_change: number
        share_count_change: number
        growth_rate_percentage: number
      }>
    }>>(`/tiktok/videos/${videoId}/analytics`)
    return response.data
  },

  // Get growth trends
  getTrends: async (boardId: string, days: number = 30) => {
    const response = await api.get<ApiResponse<{
      period: {
        start_date: string
        end_date: string
        days: number
      }
      trends: Array<{
        date: string
        total_views: number
        total_likes: number
        total_comments: number
        total_shares: number
        view_growth: number
        like_growth: number
        share_growth: number
        avg_engagement_rate: number
      }>
    }>>(`/boards/${boardId}/tiktok/analytics/trends?days=${days}`)
    return response.data
  },

  // Get performance trends for charts (per-video or overall)
  getPerformanceTrends: async (boardId: string, days: number = 30, videoId?: string) => {
    const params = new URLSearchParams({ days: days.toString() })
    if (videoId) {
      params.append('video_id', videoId)
    }
    const response = await api.get<ApiResponse<{
      views: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      likes: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      comments: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      shares: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
    }>>(`/boards/${boardId}/tiktok/analytics/performance-trends?${params}`)
    return response.data
  },

  // Get follower growth data from channel snapshots
  getFollowerGrowth: async (boardId: string, days: number = 30) => {
    const response = await api.get<ApiResponse<{
      points: Array<{
        date: string
        followers: number
        following: number
        change: number
      }>
      summary: {
        current_followers: number
        net_change: number
        growth_percent: number
        direction: 'up' | 'down' | 'neutral'
      } | null
    }>>(`/boards/${boardId}/tiktok/analytics/follower-growth?days=${days}`)
    return response.data
  },

  // Get best time to post analysis
  getBestTimeToPost: async (boardId: string) => {
    const response = await api.get<ApiResponse<{
      has_data: boolean
      message?: string
      hourly?: Array<{
        hour: number
        count: number
        avg_views: number
        avg_engagement_rate: number
      }>
      daily?: Array<{
        day: number
        name: string
        count: number
        avg_views: number
        avg_engagement_rate: number
      }>
      best_hour?: { hour: number; count: number; avg_views: number; avg_engagement_rate: number }
      best_day?: { day: number; name: string; count: number; avg_views: number; avg_engagement_rate: number }
      total_videos_analyzed?: number
    }>>(`/boards/${boardId}/tiktok/analytics/best-time-to-post`)
    return response.data
  },

  // Get content type analysis by duration
  getContentTypeAnalysis: async (boardId: string) => {
    const response = await api.get<ApiResponse<{
      buckets: Array<{
        key: string
        label: string
        count: number
        avg_views: number
        avg_likes: number
        avg_comments: number
        avg_shares: number
        avg_engagement_rate: number
        total_views: number
      }>
      best_bucket?: {
        key: string
        label: string
        count: number
        avg_engagement_rate: number
      }
      total_videos: number
    }>>(`/boards/${boardId}/tiktok/analytics/content-type-analysis`)
    return response.data
  },

  // Get AI-generated insights for TikTok analytics
  getAIInsights: async (boardId: string, days: number = 30) => {
    const response = await api.get<{
      success: boolean;
      data: {
        insights: Array<{
          icon: string;
          text: string;
          category: string;
          priority: 'high' | 'medium' | 'low';
          actionable: boolean;
        }>;
        summary: string;
      };
      cached: boolean;
      rate_limit?: {
        allowed: boolean;
        remaining: number;
        limit: number;
      };
    }>(`/boards/${boardId}/tiktok/analytics/ai-insights?days=${days}`)
    return response.data
  },
}

// Instagram Analytics API
export const instagramAnalyticsApi = {
  // Get Instagram analytics for a board
  getAnalytics: async (boardId: string, days: number = 30, page: number = 1) => {
    const response = await api.get<ApiResponse<import('@/types').InstagramAnalyticsData>>(`/boards/${boardId}/instagram/analytics?days=${days}&page=${page}`)
    return response.data
  },

  // Get detailed analytics for a specific media
  getMediaAnalytics: async (mediaId: string) => {
    const response = await api.get<ApiResponse<import('@/types').InstagramMediaAnalytics>>(`/instagram/media/${mediaId}/analytics`)
    return response.data
  },

  // Get historical analytics (snapshots over time)
  getHistoricalAnalytics: async (boardId: string, days: number = 30, mediaId?: string) => {
    const params = new URLSearchParams({ days: days.toString() })
    if (mediaId) {
      params.append('media_id', mediaId)
    }
    const response = await api.get<ApiResponse<import('@/types').InstagramHistoricalData>>(`/boards/${boardId}/instagram/analytics/history?${params}`)
    return response.data
  },

  // Get performance trends for charts
  getPerformanceTrends: async (boardId: string, days: number = 30, mediaId?: string) => {
    const params = new URLSearchParams({ days: days.toString() })
    if (mediaId) {
      params.append('media_id', mediaId)
    }
    const response = await api.get<ApiResponse<{
      reach: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      plays: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      likes: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      comments: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
      saved: {
        current: number
        change_percent: number
        trend: Array<{ date: string; value: number }>
        direction: 'up' | 'down' | 'neutral'
      }
	      shares: {
	        current: number
	        change_percent: number
	        trend: Array<{ date: string; value: number }>
	        direction: 'up' | 'down' | 'neutral'
	      }
	      engagement_rate?: {
	        current: number
	        change_percent: number
	        trend: Array<{ date: string; value: number }>
	        direction: 'up' | 'down' | 'neutral'
	      }
	      ig_reels_avg_watch_time?: {
	        current: number
	        change_percent: number
	        trend: Array<{ date: string; value: number }>
	        direction: 'up' | 'down' | 'neutral'
	      }
	      reels_skip_rate?: {
	        current: number
	        change_percent: number
	        trend: Array<{ date: string; value: number }>
	        direction: 'up' | 'down' | 'neutral'
	      }
	    }>>(`/boards/${boardId}/instagram/analytics/performance-trends?${params}`)
    return response.data
  },

  // Get follower growth from profile snapshots
  getFollowerGrowth: async (boardId: string, days: number = 30) => {
    const response = await api.get<ApiResponse<{
      points: Array<{
        date: string
        followers: number
        following: number
        change: number
      }>
      summary: {
        current_followers: number
        net_change: number
        growth_percent: number
        direction: 'up' | 'down' | 'neutral'
      } | null
    }>>(`/boards/${boardId}/instagram/analytics/follower-growth?days=${days}`)
    return response.data
  },

  // Get AI-generated analytics insights
  getAIInsights: async (boardId: string, days: number = 30) => {
    const response = await api.get<ApiResponse<{
      insights: Array<{
        icon: string
        text: string
        category: string
        priority: 'high' | 'medium' | 'low'
        actionable: boolean
      }>
      summary: string
    }>>(`/boards/${boardId}/instagram/ai-insights?days=${days}`)
    return response.data
  },

}

// Profile API
export const profileApi = {
  // Update profile (name and email)
  updateProfile: async (data: { name?: string; email?: string }) => {
    const response = await api.put<ApiResponse<{ user: any }>>('/auth/profile', data)
    return response.data
  },

  // Upload avatar
  uploadAvatar: async (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)

    const response = await api.post<ApiResponse<{ user: any; avatar_url: string }>>('/auth/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Change password
  changePassword: async (data: { current_password: string; new_password: string; new_password_confirmation: string }) => {
    const response = await api.put<ApiResponse<void>>('/auth/password', data)
    return response.data
  },
}

// Public Invitation API (for accepting/declining invitations)
export const invitationApi = {
  // View invitation details (public)
  getInvitation: async (token: string) => {
    const response = await api.get<ApiResponse<{ invitation: any }>>(`/invitations/${token}`)
    return response.data
  },

  // Accept invitation (requires auth)
  acceptInvitation: async (token: string) => {
    const response = await api.post<ApiResponse<{ board?: any; board_member?: any }>>(`/invitations/${token}/accept`)
    return response.data
  },

  // Decline invitation (public)
  declineInvitation: async (token: string) => {
    const response = await api.post<ApiResponse<void>>(`/invitations/${token}/decline`)
    return response.data
  },
}

// Dashboard API
export const dashboardApi = {
  // Get dashboard overview for a specific board
  getOverview: async (boardId?: string, days: number = 30) => {
    const params = new URLSearchParams()
    if (boardId) {
      params.append('board_id', boardId)
    }
    params.append('days', days.toString())
    const response = await api.get<ApiResponse<any>>(`/dashboard/overview?${params}`)
    return response.data
  },
}

// Blogs API (Public - no auth required)

/**
 * The blog endpoints are language-scoped server-side and default to English when
 * the request says nothing — so omitting this parameter hides every Indonesian
 * article, which is what used to make published posts look like 404s.
 */
type BlogLanguage = 'id' | 'en'

const resolveBlogLanguage = (override?: string): BlogLanguage => {
  const raw = override ?? i18n.language ?? ''
  const short = raw.slice(0, 2).toLowerCase()
  return short === 'id' ? 'id' : 'en'
}

const otherLanguage = (language: BlogLanguage): BlogLanguage =>
  language === 'id' ? 'en' : 'id'

export const blogsApi = {
  // Get all blogs with pagination
  getAll: async (page: number = 1, language?: string) => {
    const primary = resolveBlogLanguage(language)
    const request = (lang: BlogLanguage) => {
      const params = new URLSearchParams({ page: page.toString(), language: lang })
      return publicApi.get<import('@/types').BlogListResponse>(`/blogs?${params}`)
    }

    const response = await request(primary)
    if ((response.data?.meta?.total ?? 0) > 0) return response.data

    // Nothing published in the reader's language. An empty blog index is worse
    // than one in the other language — and Googlebot renders as en-US, so
    // without this it would index the blog as having no articles at all.
    const fallback = await request(otherLanguage(primary))
    return (fallback.data?.meta?.total ?? 0) > 0 ? fallback.data : response.data
  },

  // Get single blog by slug
  getBySlug: async (slug: string, language?: string) => {
    const primary = resolveBlogLanguage(language)
    const request = (lang: BlogLanguage) => {
      const params = new URLSearchParams({ language: lang })
      return publicApi.get<import('@/types').BlogDetailResponse>(
        `/blogs/${encodeURIComponent(slug)}?${params}`
      )
    }

    try {
      const response = await request(primary)
      return response.data
    } catch (error) {
      // A shared link must resolve regardless of the reader's UI language, so a
      // miss in one language is retried in the other before giving up.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        const fallback = await request(otherLanguage(primary))
        return fallback.data
      }
      throw error
    }
  },
}

// Video Proxy API (for extracting Instagram/TikTok videos via official APIs)
export const videoApi = {
  // Extract direct video URL from Instagram/TikTok using connected account
  extractVideo: async (url: string, boardId: string) => {
    const response = await publicApi.post<{
      video_url?: string;
      thumbnail_url?: string;
      platform?: string;
      content_type?: 'reels' | 'photo' | 'carousel' | 'video';
      carousel_urls?: string[];
      error?: string;
      embed_html?: string;
      embed_url?: string;  // TikTok iframe embed URL
      cover_url?: string;
      fallback_url?: string;
    }>('/video/extract', { url, board_id: boardId })
    return response.data
  },

  // Get proxied video URL (to avoid CORS)
  getProxyUrl: (videoUrl: string) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    return `${baseUrl}/video/proxy?url=${encodeURIComponent(videoUrl)}`
  },

  /**
   * Get a stable, playable URL for any uploaded/stored media asset.
   * Always routes through the backend proxy so the URL works regardless of
   * whether it was stored as a MinIO URL (:9100), a ngrok URL, a localhost
   * /storage/... URL, or any other format.
   *
   * Use this instead of using raw `platform_urls.uploaded` in <video> tags.
   */
  getUploadedMediaUrl: (uploadedUrl: string): string => {
    if (!uploadedUrl) return uploadedUrl;
    // blob: URLs are local browser objects — cannot be proxied, pass through
    if (uploadedUrl.startsWith('blob:')) return uploadedUrl;
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    if (uploadedUrl.startsWith(`${baseUrl}/video/proxy`)) return uploadedUrl;
    return `${baseUrl}/video/proxy?url=${encodeURIComponent(uploadedUrl)}`
  },
}

// Media API
export interface MediaUploadResult {
  id: string
  path: string
  url: string
  original_name: string
  mime_type: string
  size: number
  uploaded_at: string
}

interface PresignResult {
  id: string
  key: string
  upload_url: string
  headers: Record<string, string>
  expires_in: number
}

export const mediaApi = {
  // Upload media file (proxied through the API server — used for small files
  // and as the fallback when the presigned/direct flow is unavailable)
  upload: async (file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<ApiResponse<{
      id: string
      path: string
      url: string
      original_name: string
      mime_type: string
      size: number
      uploaded_at: string
    }>>('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      },
    })
    return response.data
  },

  /**
   * Direct-to-R2 upload for scheduled-content media (video/image attached to a
   * card). The bytes go straight from the browser to object storage, so large
   * files never pass through the API server.
   *
   * 1. POST /media/presign  -> short-lived signed PUT URL (server picks the key
   *                            and pins Content-Type into the signature)
   * 2. PUT  <upload_url>    -> raw file body, replaying the signed headers
   * 3. POST /media/confirm  -> server verifies the object, returns the same
   *                            payload shape as the proxied upload()
   *
   * Small images that are not scheduled content (avatars, blog images) keep
   * using their own endpoints — this flow is not for them.
   *
   * Falls back to the proxied upload() when the backend has no S3/R2 media disk
   * configured (422) or the endpoint is not deployed yet (404).
   */
  uploadDirect: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<MediaUploadResult>> => {
    let presign: PresignResult
    try {
      const presignResponse = await api.post<ApiResponse<PresignResult>>('/media/presign', {
        content_type: file.type,
        size: file.size,
      })
      presign = presignResponse.data.data
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 404 || status === 422) {
        console.warn('Presigned upload unavailable, falling back to proxied upload.', error?.response?.data)
        return await mediaApi.upload(file, onProgress) as ApiResponse<MediaUploadResult>
      }
      throw error
    }

    // Bare axios: the signed PUT must not carry our Authorization / JSON
    // defaults. Content-Type isn't signed, but storage keeps whatever it is
    // sent, and axios would otherwise send form data.
    await axios.put(presign.upload_url, file, {
      headers: { ...presign.headers, 'Content-Type': file.type || 'application/octet-stream' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          // Leave the last slice of the bar for the confirm round-trip
          const percentCompleted = Math.round((progressEvent.loaded * 95) / progressEvent.total)
          onProgress(percentCompleted)
        }
      },
    })

    const confirmResponse = await api.post<ApiResponse<Omit<MediaUploadResult, 'original_name'>>>(
      '/media/confirm',
      { key: presign.key }
    )
    onProgress?.(100)

    return {
      ...confirmResponse.data,
      data: {
        // confirm() has no original name — the server never sees it
        original_name: file.name,
        ...confirmResponse.data.data,
      },
    }
  },

  /**
   * @deprecated Scheduled-content media now uses uploadDirect() + a card update
   * to attach the URL. Kept for any flow still relying on the proxied endpoint.
   */
  uploadToCard: async (
    boardId: string,
    stageId: string,
    cardId: string,
    file: File,
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<ApiResponse<{
      id: string
      path: string
      url: string
      original_name: string
      mime_type: string
      size: number
      uploaded_at: string
      card: Card
    }>>(`/boards/${boardId}/stages/${stageId}/cards/${cardId}/media`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      },
    })
    return response.data
  },
}

export default api
